"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useTitle } from "@/contexts/TitleContext";
import { useUnreadIndicator } from "@/hooks/useUnreadIndicator";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useAuth } from "../auth/Auth";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import { useNotificationsContext } from "../notifications/NotificationsContext";
import { isNavItemActive } from "./isNavItemActive";
import { useViewContext } from "./ViewContext";
import type { NavItem as NavItemData } from "./navTypes";
interface Props {
  readonly item: NavItemData;
  readonly isCurrentWaveDm?: boolean;
}
const NavItem = t0 => {
  const $ = _c(70);
  const {
    item,
    isCurrentWaveDm: t1
  } = t0;
  const isCurrentWaveDm = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    activeView,
    handleNavClick
  } = useViewContext();
  const {
    name
  } = item;
  const {
    icon
  } = item;
  const {
    address,
    seizeConnect
  } = useSeizeConnectContext();
  const {
    connectedProfile
  } = useAuth();
  let t2;
  if ($[0] !== connectedProfile?.handle || $[1] !== connectedProfile?.normalised_handle) {
    t2 = (connectedProfile?.normalised_handle ?? connectedProfile?.handle)?.toLowerCase();
    $[0] = connectedProfile?.handle;
    $[1] = connectedProfile?.normalised_handle;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const normalizedConnectedHandle = t2;
  let t3;
  if ($[3] !== address) {
    t3 = address?.toLowerCase();
    $[3] = address;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const normalizedConnectedAddress = t3;
  const profileSlug = normalizedConnectedHandle ?? normalizedConnectedAddress;
  const profileHref = profileSlug ? `/${profileSlug}` : null;
  const {
    setTitle
  } = useTitle();
  const {
    notifications,
    haveUnreadNotifications
  } = useUnreadNotifications(item.name === "Notifications" ? connectedProfile?.handle ?? null : null);
  const t4 = item.name === "Messages" ? connectedProfile?.handle ?? null : null;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      type: "messages",
      handle: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    hasUnread: hasUnreadMessages
  } = useUnreadIndicator(t5);
  const {
    removeAllDeliveredNotifications
  } = useNotificationsContext();
  let t6;
  if ($[7] !== haveUnreadNotifications || $[8] !== item.name || $[9] !== notifications?.unread_count || $[10] !== removeAllDeliveredNotifications || $[11] !== setTitle) {
    t6 = () => {
      if (item.name !== "Notifications") {
        return;
      }
      if (haveUnreadNotifications) {
        setTitle(`(${notifications?.unread_count}) Notifications | 6529.io`);
      }
      if (!haveUnreadNotifications) {
        removeAllDeliveredNotifications();
        setTitle("Notifications | 6529.io");
      }
    };
    $[7] = haveUnreadNotifications;
    $[8] = item.name;
    $[9] = notifications?.unread_count;
    $[10] = removeAllDeliveredNotifications;
    $[11] = setTitle;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = notifications?.unread_count;
  let t8;
  if ($[13] !== haveUnreadNotifications || $[14] !== item.name || $[15] !== removeAllDeliveredNotifications || $[16] !== setTitle || $[17] !== t7) {
    t8 = [haveUnreadNotifications, item.name, t7, removeAllDeliveredNotifications, setTitle];
    $[13] = haveUnreadNotifications;
    $[14] = item.name;
    $[15] = removeAllDeliveredNotifications;
    $[16] = setTitle;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  useEffect(t6, t8);
  if (item.disabled) {
    let t9;
    if ($[19] !== icon || $[20] !== item.iconComponent || $[21] !== item.iconSizeClass || $[22] !== name) {
      t9 = <div className="tw-mt-4 tw-flex tw-h-9 tw-items-center tw-justify-center">{item.iconComponent ? <item.iconComponent className={`${item.iconSizeClass ?? "tw-size-7"} tw-text-iron-500`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={item.iconSizeClass ?? "tw-size-7"} />}</div>;
      $[19] = icon;
      $[20] = item.iconComponent;
      $[21] = item.iconSizeClass;
      $[22] = name;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== name || $[25] !== t9) {
      t10 = <button type="button" aria-label={name} aria-disabled="true" disabled={true} className="tw-pointer-events-none tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-opacity-40 tw-transition-colors focus:tw-outline-none">{t9}</button>;
      $[24] = name;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    return t10;
  }
  const iconSizeClass = item.iconSizeClass ?? "tw-size-7";
  const isProfileItem = item.kind === "route" && item.name === "Profile";
  const t9 = pathname ?? "";
  let t10;
  if ($[27] !== activeView || $[28] !== isProfileItem || $[29] !== profileHref || $[30] !== t9) {
    const normalizedPathname = t9.toLowerCase();
    t10 = isProfileItem && activeView === null && profileHref !== null && (normalizedPathname === profileHref || normalizedPathname.startsWith(`${profileHref}/`));
    $[27] = activeView;
    $[28] = isProfileItem;
    $[29] = profileHref;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const isProfileActive = t10;
  let t11;
  if ($[32] !== activeView || $[33] !== isCurrentWaveDm || $[34] !== isProfileActive || $[35] !== isProfileItem || $[36] !== item || $[37] !== pathname || $[38] !== searchParams) {
    t11 = isProfileItem ? isProfileActive : isNavItemActive(item, pathname ?? "", searchParams ?? new URLSearchParams(), activeView, isCurrentWaveDm);
    $[32] = activeView;
    $[33] = isCurrentWaveDm;
    $[34] = isProfileActive;
    $[35] = isProfileItem;
    $[36] = item;
    $[37] = pathname;
    $[38] = searchParams;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const isActive = t11;
  let t12;
  if ($[40] !== address || $[41] !== handleNavClick || $[42] !== item || $[43] !== profileHref || $[44] !== seizeConnect) {
    t12 = () => {
      if (item.kind === "route" && item.name === "Profile") {
        if (!address) {
          seizeConnect();
          return;
        }
        handleNavClick({
          ...item,
          href: profileHref ?? item.href
        });
        return;
      }
      handleNavClick(item);
    };
    $[40] = address;
    $[41] = handleNavClick;
    $[42] = item;
    $[43] = profileHref;
    $[44] = seizeConnect;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const handleClick = t12;
  const t13 = isActive ? "page" : undefined;
  let t14;
  if ($[46] !== isActive) {
    t14 = isActive && <motion.div layoutId="nav-indicator" className="tw-absolute tw-left-0 tw-top-0 tw-h-0.5 tw-w-full tw-rounded-full tw-bg-white" />;
    $[46] = isActive;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== icon || $[49] !== iconSizeClass || $[50] !== isActive || $[51] !== item.iconComponent || $[52] !== name) {
    t15 = item.iconComponent ? <item.iconComponent className={`${iconSizeClass} ${isActive ? "tw-text-white" : "tw-text-iron-500"}`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={iconSizeClass} />;
    $[48] = icon;
    $[49] = iconSizeClass;
    $[50] = isActive;
    $[51] = item.iconComponent;
    $[52] = name;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== haveUnreadNotifications || $[55] !== item.name) {
    t16 = item.name === "Notifications" && haveUnreadNotifications && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[54] = haveUnreadNotifications;
    $[55] = item.name;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== hasUnreadMessages || $[58] !== item.name) {
    t17 = item.name === "Messages" && hasUnreadMessages && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[57] = hasUnreadMessages;
    $[58] = item.name;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== t15 || $[61] !== t16 || $[62] !== t17) {
    t18 = <div className="tw-relative tw-mt-4 tw-flex tw-h-9 tw-items-center tw-justify-center">{t15}{t16}{t17}</div>;
    $[60] = t15;
    $[61] = t16;
    $[62] = t17;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== handleClick || $[65] !== name || $[66] !== t13 || $[67] !== t14 || $[68] !== t18) {
    t19 = <button type="button" aria-label={name} aria-current={t13} onClick={handleClick} className="tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-transition-colors focus:tw-outline-none">{t14}{t18}</button>;
    $[64] = handleClick;
    $[65] = name;
    $[66] = t13;
    $[67] = t14;
    $[68] = t18;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  return t19;
};
export default NavItem;